/**
 * Determines if a phone number is toll-free based on common toll-free prefixes
 * @param phoneNumber - The phone number to check (can include +1 prefix)
 * @returns true if the number is toll-free, false otherwise
 */
export const isTollFreeNumber = (phoneNumber: string): boolean => {
  // Remove any non-digit characters except + and 1
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, "");

  // Common toll-free prefixes
  const tollFreePrefixes = [
    "800",
    "888",
    "877",
    "866",
    "855",
    "844",
    "833",
    "822",
    "880",
    "881",
    "882",
    "883",
    "884",
    "885",
    "886",
    "887",
    "889",
  ];

  // Check if the number starts with +1 or 1 followed by a toll-free prefix
  if (cleanNumber.startsWith("+1")) {
    const numberWithoutPrefix = cleanNumber.substring(2);
    return tollFreePrefixes.some((prefix) =>
      numberWithoutPrefix.startsWith(prefix)
    );
  } else if (cleanNumber.startsWith("1")) {
    const numberWithoutPrefix = cleanNumber.substring(1);
    return tollFreePrefixes.some((prefix) =>
      numberWithoutPrefix.startsWith(prefix)
    );
  }

  // If no country code, check if it starts with a toll-free prefix
  return tollFreePrefixes.some((prefix) => cleanNumber.startsWith(prefix));
};

/**
 * Gets the package type based on phone number type
 * @param phoneNumber - The phone number to check
 * @returns "toll_free" or "regular"
 */
export const getPackageTypeFromPhoneNumber = (
  phoneNumber: string
): "toll_free" | "regular" => {
  return isTollFreeNumber(phoneNumber) ? "toll_free" : "regular";
};

/**
 * Ensures a phone number has the +1 prefix
 * @param phoneNumber - The phone number to format
 * @returns The phone number with +1 prefix
 */
export const ensurePhoneNumberFormat = (phoneNumber: string): string => {
  // Remove any existing +1 prefix and non-digit characters
  const cleanNumber = phoneNumber.replace(/[^\d]/g, "");

  // If 11 digits and starts with 1, return just the digits
  if (cleanNumber.length === 11 && cleanNumber.startsWith("1")) {
    return cleanNumber;
  }

  // Else if 10 digits, add "1" to start (without +)
  if (cleanNumber.length === 10) {
    const result = `1${cleanNumber}`;
    return result;
  }

  // Return the original if we can't determine the format
  return phoneNumber;
};

/**
 * Formats a phone number to display format: +1 (123) 456-7890
 * @param phoneNumber - The phone number to format (can be with or without +1)
 * @returns The formatted phone number string
 */
export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
  // Remove any non-digit characters
  const cleanNumber = phoneNumber.replace(/[^\d]/g, "");

  // Handle different input formats
  let digits = cleanNumber;

  // If 11 digits and starts with 1, use as is
  if (cleanNumber.length === 11 && cleanNumber.startsWith("1")) {
    digits = cleanNumber;
  }
  // If 10 digits, add 1 prefix
  else if (cleanNumber.length === 10) {
    digits = `1${cleanNumber}`;
  }
  // If already has +1 prefix, extract digits
  else if (phoneNumber.startsWith("+1")) {
    digits = phoneNumber.replace(/[^\d]/g, "");
  }
  // If can't determine format, return original
  else if (cleanNumber.length < 10) {
    return phoneNumber;
  }

  // Format as +1 (123) 456-7890
  if (digits.length === 11 && digits.startsWith("1")) {
    const areaCode = digits.substring(1, 4);
    const exchange = digits.substring(4, 7);
    const number = digits.substring(7, 11);
    return `+1 (${areaCode}) ${exchange}-${number}`;
  }

  // Return original if formatting fails
  return phoneNumber;
};

/**
 * Formats a phone number for input (removes +1 prefix for form inputs)
 * @param phoneNumber - The phone number to format
 * @returns The phone number without +1 prefix for form input
 */
export const formatPhoneNumberForInput = (phoneNumber: string): string => {
  // Remove +1 prefix and format as (123) 456-7890
  const cleanNumber = phoneNumber.replace(/[^\d]/g, "");

  if (cleanNumber.length === 11 && cleanNumber.startsWith("1")) {
    const areaCode = cleanNumber.substring(1, 4);
    const exchange = cleanNumber.substring(4, 7);
    const number = cleanNumber.substring(7, 11);
    return `(${areaCode}) ${exchange}-${number}`;
  }

  if (cleanNumber.length === 10) {
    const areaCode = cleanNumber.substring(0, 3);
    const exchange = cleanNumber.substring(3, 6);
    const number = cleanNumber.substring(6, 10);
    return `(${areaCode}) ${exchange}-${number}`;
  }

  return phoneNumber;
};
